/**
 * Storefront v7 — the story builder (pure; executed under node by the contracts).
 *
 * Turns the tenant's data into the chapters of the cinematic stage:
 *
 *   hello   the merchant's own hero photo, headline, one primary action, a fact line
 *   fresh   the floating ingredient photographs the merchant stored (or four of
 *           their real dish photos), with short highlights the merchant wrote
 *   choice  what the kitchen sells: plans (meal prep), categories (restaurant)
 *           or packages (catering), with real dish photographs beside them
 *   packed  the finished meal / real packaging photo, the fulfilment facts the
 *           tenant actually configured, and the next action
 *
 * A chapter with nothing to show is DROPPED — the stage shrinks, the pills
 * shrink, nothing is invented. Every headline is either the merchant's own
 * words or neutral structural copy that makes no claim.
 */
import { money } from '../primitives';

/**
 * The platform's meal-pass flavoured defaults (backend/models/meal_pass/
 * meal_pass_storefront_config.py and the two derived-defaults dicts). A tenant
 * that never touched these keys carries them verbatim; they must NEVER reach a
 * storefront that does not sell passes ("Browse passes" on a restaurant's
 * primary button). A contract pins this table against the Pydantic defaults.
 */
export const PASS_DEFAULT_COPY = {
  hero_title: ['Buy meals in advance. Save every time.'],
  hero_italic: ['meal passes', 'YofoPasses'],
  hero_subtitle: [
    'Buy today, redeem forever. Credits never expire, and every dollar stays with the kitchen you love.',
    'Redeem anytime — dine in, pickup, or delivery',
  ],
  hero_cta_primary: ['Browse passes'],
  hero_cta_secondary: ['Our story'],
  story_eyebrow: ['A small ritual'],
  story_headline: ['A meal you already own,'],
  story_headline_italic: ['waiting on you.'],
  steps_headline: ['How Meal Passes', 'How YofoPass works'],
  steps_headline_italic: ['work'],
  punchline_headline: ['Every meal, already'],
  punchline_headline_italic: ['yours.'],
  punchline_body: ['Buy today. Come back whenever hunger, tradition, or a bad Tuesday calls for it.'],
  punchline_cta: ['Pick your pass'],
  closing_slab_headline: ['Come hungry.'],
  closing_slab_headline_italic: ['Leave full.'],
};

/** The merchant's own words for a copy key — or null when the value is a pass-flavoured platform default and the page sells no passes. */
export const ownCopy = (brand, key, { passes = false } = {}) => {
  const v = brand && brand[key];
  if (typeof v !== 'string' || !v.trim()) return null;
  const defaults = PASS_DEFAULT_COPY[key];
  if (defaults && !passes && defaults.includes(v.trim())) return null;
  return v.trim();
};

/** Neutral structural words per business model — a statement of what the page sells, never a claim. */
export const STRUCTURAL = {
  emphasis: { meal_prep: 'Meal plans.', restaurant: 'Order online.', catering: 'Catering.', bakery: 'Order for pickup.' },
  choiceLabel: { meal_prep: 'Your plans', restaurant: 'Your menu', catering: 'Your packages', bakery: 'Your menu' },
  choiceHeadline: { meal_prep: 'Pick a plan.', restaurant: 'Pick a dish.', catering: 'Pick a package.', bakery: 'Pick your order.' },
  freshHeadline: 'What goes in.',
  packedHeadline: 'Ready when you are.',
  packedLabel: 'Ready',
  freshLabel: 'What goes in',
};

const first = (arr) => (Array.isArray(arr) && arr.length ? arr[0] : null);
const photoOf = (it) => (it && (it.image_url || first(it.images))) || null;
const priceOfPlan = (plan) => {
  if (plan?.storefront_selling?.pricing_mode === 'category_meal') return { price: plan.storefront_selling.composed_meal.base_price, perDish: true };
  if (plan?.storefront_selling?.pricing_mode === 'selected_items') {
    const prices = (plan.storefront_selling.item_prices || []).map(p => Number(p.one_time_price)).filter(p => p > 0);
    return prices.length ? { price: Math.min(...prices), perDish: true } : null;
  }
  const tiers = Array.isArray(plan?.pricing?.tiers) ? plan.pricing.tiers.filter((t) => Number(t?.price) > 0) : [];
  if (!tiers.length) return null;
  return tiers.reduce((a, b) => (Number(b.price) < Number(a.price) ? b : a));
};
const sentence = (text, max = 90) => {
  const t = String(text || '').trim();
  if (!t) return '';
  const cut = t.split(/(?<=[.!?])\s/)[0];
  return cut.length > max ? `${cut.slice(0, max - 1).trim()}…` : cut;
};

/** "12 dishes · 4 categories · Pickup" — counts from the catalogue, never adjectives. */
export const factLine = ({ model, plans = [], items = [], packages = [], categories = [], currency, pickupAvailable, deliveryAvailable }) => {
  const parts = [];
  if (model === 'meal_prep' && plans.length) {
    const cheapest = plans.map(priceOfPlan).filter(Boolean).sort((a, b) => Number(a.price) - Number(b.price))[0];
    parts.push(`${plans.length} ${plans.length === 1 ? 'plan' : 'plans'}`);
    if (cheapest) parts.push(`from ${money(cheapest.price, currency)}${cheapest.perDish ? ' / dish' : ''}`);
  } else if (model === 'catering' && packages.length) {
    const cheapest = packages.map((p) => Number(p.price_per_person)).filter((n) => n > 0).sort((a, b) => a - b)[0];
    parts.push(`${packages.length} ${packages.length === 1 ? 'package' : 'packages'}`);
    if (cheapest) parts.push(`from ${money(cheapest, currency)} per person`);
  } else if (items.length) {
    parts.push(`${items.length} ${items.length === 1 ? 'dish' : 'dishes'}`);
    if (categories.length > 1) parts.push(`${categories.length} categories`);
  }
  if (pickupAvailable) parts.push('Pickup');
  if (deliveryAvailable) parts.push('Delivery');
  return parts.join(' · ');
};

const DAY = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const tidy = (t) => String(t || '').trim().replace(/^0(\d:)/, '$1');

/** "Mon–Fri 9:00–21:00 · Sat 10:00–16:00" from the merchant's own weekly hours; closed days are simply absent. */
export const hoursSummary = (settings) => {
  if (!settings || typeof settings !== 'object' || !settings.store_hours_enabled || !settings.operating_hours || typeof settings.operating_hours !== 'object') return null;
  const spans = [];
  for (const d of ORDER) {
    const h = settings.operating_hours[d];
    const open = h && h.closed !== true && h.is_closed !== true && h.open && h.close ? `${tidy(h.open)}–${tidy(h.close)}` : null;
    const last = spans[spans.length - 1];
    if (open && last && last.text === open && last.endIdx === ORDER.indexOf(d) - 1) { last.end = d; last.endIdx = ORDER.indexOf(d); } else if (open) spans.push({ start: d, end: d, endIdx: ORDER.indexOf(d), text: open });
  }
  if (!spans.length) return null;
  return spans.map((s) => `${DAY[s.start]}${s.end !== s.start ? `–${DAY[s.end]}` : ''} ${s.text}`).join(' · ');
};

/** The fulfilment facts the tenant actually configured — each one absent when its data is. */
export const fulfilmentFacts = ({ settings: rawSettings, currency, pickupAvailable, deliveryAvailable }) => {
  const settings = rawSettings && typeof rawSettings === 'object' ? rawSettings : {};
  const facts = [];
  const hours = hoursSummary(settings);
  if (hours) facts.push({ kind: 'hours', label: 'Hours', text: hours });
  if (pickupAvailable && settings.pickup_address) facts.push({ kind: 'pickup', label: 'Pickup', text: settings.pickup_address });
  if (deliveryAvailable) facts.push({ kind: 'delivery', label: 'Delivery', text: 'Check your address' });
  if (Number(settings.order_prep_time_minutes) > 0) facts.push({ kind: 'prep', label: 'Ready in', text: `${settings.order_prep_time_minutes} min` });
  if (Number(settings.minimum_order_value) > 0) facts.push({ kind: 'minimum', label: 'Minimum order', text: money(settings.minimum_order_value, currency) });
  return facts;
};

const resolveFeatured = (ids, items) => {
  const wanted = Array.isArray(ids) ? ids : [];
  return wanted.map((id) => items.find((it) => it && it.id === id)).filter((it) => it && photoOf(it));
};

/**
 * buildChapters(input) → chapter[]
 * input: { model, brand, settings, plans, items, packages, categories, currency,
 *          showPasses, orderable, pickupAvailable, deliveryAvailable, tenantName, ctas }
 */
/** The catalogue's OWN generated photographs, by URL. The storefront cannot read
 *  provenance off a brand photo's URL, so this is how a picture stored before any
 *  provenance key existed is still recognised: it IS one of these files. */
export const generatedPhotoUrls = (items = []) => new Set(
  (Array.isArray(items) ? items : []).filter((it) => it && it.image_source === 'ai' && photoOf(it)).map(photoOf),
);

export const buildChapters = (input) => {
  const {
    model = 'restaurant', brand: rawBrand, settings: rawSettings, plans = [], items = [], packages = [], categories = [],
    currency = 'CAD', showPasses = false, orderable = 0, pickupAvailable = false, deliveryAvailable = false, fulfilmentOn = true, tenantName = '', ctas = {},
    aiPhotoUrls: providedAiUrls = null,
  } = input || {};
  const brand = rawBrand && typeof rawBrand === 'object' ? rawBrand : {};
  const settings = rawSettings && typeof rawSettings === 'object' ? rawSettings : {};
  const own = (k) => ownCopy(brand, k, { passes: showPasses });
  const copyFor = (id) => ((brand.story_chapters && typeof brand.story_chapters === 'object' && brand.story_chapters[id] && typeof brand.story_chapters[id] === 'object') ? brand.story_chapters[id] : {});
  const chapters = [];
  const photographed = items.filter((it) => it && photoOf(it));
  const gallery = (Array.isArray(brand.gallery_items) ? brand.gallery_items : []).filter((g) => g && g.image_url);
  const heroPhoto = brand.hero_image_url || photoOf(photographed[0]) || (gallery[0] && gallery[0].image_url) || null;
  // THE HONESTY RULE FOR PICTURES, and the presence of a URL says nothing about it:
  // the launch engine PREFERS a generated dish photo for `hero_image_url`, so a brand
  // photo is generated when it SAYS so (`hero_image_source`), or when it is one of the
  // catalogue's own AI photographs - which is how a row written before that key existed
  // still gets disclosed, without a migration and without trusting a marker younger
  // than the data (PR #105's rule: read the evidence that is already there).
  // The caller may hand in the set computed from the FULL catalogue: `items` here can
  // be GATED (a storefront with its menu switched off passes none), and provenance is
  // not a question about what the page sells. Falling back keeps the builder pure.
  const aiPhotoUrls = providedAiUrls instanceof Set ? providedAiUrls : generatedPhotoUrls(items);
  const isGeneratedUrl = (url) => !!url && aiPhotoUrls.has(url);
  const generatedBrandPhoto = (url, source) => !!url && (source === 'ai' || isGeneratedUrl(url));
  const heroGenerated = brand.hero_image_url
    ? generatedBrandPhoto(brand.hero_image_url, brand.hero_image_source)
    : (photographed[0]
      ? photographed[0].image_source === 'ai'
      : (gallery[0] ? (gallery[0].image_source === 'ai' || isGeneratedUrl(gallery[0].image_url)) : false));
  const brandName = (brand.brand_name || tenantName || '').trim();
  const primaryCta = own('hero_cta_primary') || ctas.primary || 'Order now';
  const primaryTarget = ctas.primaryTarget || (orderable > 0 ? 'order' : 'menu');

  // 1 — hello: what food, how to get it, where to start
  const c1 = copyFor('hello');
  chapters.push({
    id: 'hello',
    label: c1.label || brandName || 'Welcome',
    tint: 'paper',
    eyebrow: c1.eyebrow || null,
    title: c1.headline || own('hero_title') || brandName,
    emphasis: c1.italic || own('hero_italic') || STRUCTURAL.emphasis[model] || STRUCTURAL.emphasis.restaurant,
    sub: c1.sub || own('hero_subtitle') || (brand.tagline || '').trim() || null,
    photo: heroPhoto,
    photoSource: brand.hero_image_url ? 'merchant' : (photographed[0] ? 'dish' : (gallery[0] ? 'gallery' : null)),
    generated: heroGenerated,
    fact: factLine({ model, plans, items, packages, categories, currency, pickupAvailable, deliveryAvailable }),
    cta: primaryCta,
    ctaTarget: primaryTarget,
    secondary: own('hero_cta_secondary') || ctas.secondary || null,
    secondaryTarget: ctas.secondaryTarget || 'menu',
  });

  // 2 — fresh: the merchant's stored ingredient cut-outs, else four real dishes
  const ingredients = (Array.isArray(brand.story_ingredients) ? brand.story_ingredients : []).filter((i) => i && i.image_url).slice(0, 8);
  const c2 = copyFor('fresh');
  if (ingredients.length >= 3) {
    chapters.push({
      id: 'fresh', label: c2.label || STRUCTURAL.freshLabel, tint: 'ink', eyebrow: c2.eyebrow || null,
      title: c2.headline || STRUCTURAL.freshHeadline, emphasis: c2.italic || '', sub: c2.sub || null,
      floaters: ingredients.map((i) => ({ image_url: i.image_url, label: String(i.label || '').trim(), note: String(i.note || '').trim(), generated: i.image_source === 'ai' })),
      generated: ingredients.some((i) => i.image_source === 'ai'),
    });
  } else if (photographed.length >= 4) {
    chapters.push({
      id: 'fresh', label: c2.label || STRUCTURAL.freshLabel, tint: 'ink', eyebrow: c2.eyebrow || null,
      title: c2.headline || STRUCTURAL.freshHeadline, emphasis: c2.italic || '', sub: c2.sub || null,
      dishes: photographed.slice(0, 4).map((it) => ({ id: it.id, name: it.name, image_url: photoOf(it), generated: it.image_source === 'ai' })),
      generated: photographed.slice(0, 4).some((it) => it.image_source === 'ai'),
    });
  }

  // 3 — choice: what the kitchen sells, from the catalogue
  const c3 = copyFor('choice');
  let rows = [];
  let tiles = [];
  if (model === 'meal_prep' && plans.length) {
    const featuredId = typeof brand.featured_plan_id === 'string' ? brand.featured_plan_id : null;
    const ordered = [...plans].sort((a, b) => (a.id === featuredId ? -1 : b.id === featuredId ? 1 : 0));
    rows = ordered.slice(0, 3).map((p) => {
      const t = priceOfPlan(p);
      return { id: p.id, kind: 'plan', name: p.alias || p.name, sub: sentence(p.description) || (t ? `${money(t.price, currency)}${t.perDish ? ' / dish' : t.quantity ? ` for ${t.quantity} meals` : ''}` : ''), featured: p.id === featuredId };
    });
  } else if (model === 'catering' && packages.length) {
    rows = packages.slice(0, 3).map((p) => ({ id: p.id, kind: 'package', name: p.name, sub: Number(p.price_per_person) > 0 ? `from ${money(p.price_per_person, currency)} per person` : sentence(p.description), featured: false }));
  } else if (categories.length) {
    // the fullest categories lead (a restaurant's story is its mains, not its drinks)
    rows = [...categories].sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0)).slice(0, 3).map((c) => ({ id: c.key, kind: 'category', name: c.label, sub: `${c.count} ${c.count === 1 ? 'dish' : 'dishes'}`, featured: false }));
  }
  const featuredTiles = resolveFeatured(brand.featured_item_ids, items);
  tiles = [...featuredTiles, ...photographed.filter((it) => !featuredTiles.includes(it))].slice(0, 4).map((it) => ({ id: it.id, name: it.name, image_url: photoOf(it), generated: it.image_source === 'ai' }));
  if (rows.length) {
    chapters.push({
      id: 'choice', label: c3.label || STRUCTURAL.choiceLabel[model] || STRUCTURAL.choiceLabel.restaurant, tint: 'primary', eyebrow: c3.eyebrow || null,
      title: c3.headline || STRUCTURAL.choiceHeadline[model] || STRUCTURAL.choiceHeadline.restaurant, emphasis: c3.italic || '', sub: c3.sub || null,
      rows, tiles, generated: tiles.some((t) => t.generated),
    });
  }

  // 4 — packed: the merchant's finished meal / real packaging, the configured facts, the next action
  const c4 = copyFor('packed');
  const facts = fulfilmentFacts({ settings, currency, pickupAvailable, deliveryAvailable });
  // the address check is offered only while the fulfilment card is on the page to scroll to
  const canCheckAddress = deliveryAvailable && fulfilmentOn !== false;
  chapters.push({
    id: 'packed', label: c4.label || STRUCTURAL.packedLabel, tint: 'paper', eyebrow: c4.eyebrow || null,
    title: c4.headline || STRUCTURAL.packedHeadline, emphasis: c4.italic || '', sub: c4.sub || null,
    photo: brand.story_finish_image_url || heroPhoto,
    generated: brand.story_finish_image_url
      ? generatedBrandPhoto(brand.story_finish_image_url, brand.story_finish_image_source)
      : heroGenerated,
    caption: brand.story_finish_caption ? String(brand.story_finish_caption).trim() : null,
    facts,
    cta: primaryCta, ctaTarget: primaryTarget,
    secondary: canCheckAddress ? 'Check my address' : null, secondaryTarget: canCheckAddress ? 'fulfilment' : null,
  });

  return chapters;
};

export default buildChapters;
